import type { Maybe, Cascadify } from './util'
import type { Status } from './common'
import type { User } from './user'
import type { ActivityStep, CascadeActivityStep } from './activity-step'
import type { ActivityMedia, CascadeActivityMedia } from './activity-media'
import type { ActivityGroup } from './activity-group'
import type { Room } from './room'

export enum ActivityType {
  MagicNumber = 'magic-number',
  Roulette = 'roulette',
  Craps = 'craps',
  TextEntry = 'text-entry',
  MultipleChoice = 'multiple-choice',
  Freeform = 'freeform',
  Giphy = 'giphy',
  Categories = 'categories',
  BuzzIn = 'buzz-in',
  TeamPhoto = 'team-photo',
  TeamName = 'team-name',
  TakePhoto = 'take-photo',
  Default = 'default',
  SpiralDrawing = 'spiral-drawing',
  DiscussionGroup = 'discussion-group',
  QuestionCards = 'question-cards',
  WordCloud = 'word-cloud',
  RatingPoll = 'rating-poll',
  DrawingEachTeam = 'drawing-each-team',
  DrawingPictionary = 'drawing-pictionary',
  Finale = 'finale',
  Charades = 'charades',
  Lipdub = 'lipdub',
  PhotoBooth = 'photo-booth',
  VideoIndividual = 'video-individual',
  VideoTeam = 'video-team',
  PlayerRating = 'player-rating',
  Match = 'match',
  FactMatch = 'fact-match',
  FamilyFeud = 'family-feud',
  OrderTheCards = 'order-the-cards',
  RoyalRumble = 'royal-rumble',
  WhiteElephant = 'white-elephant',
  Poll = 'poll',
  Award = 'award',
  Slides = 'slides',
  TelephonePictionary = 'telephone-pictionary',
  PriceIsRight = 'price-is-right',
  RaiseTheStakes = 'raise-the-stakes',
  TwoTruths = 'two-truths',
  TwoTruthsReveal = 'two-truths-reveal',
  YouTube = 'youtube',
  HandMovement = 'hand-movement',
  UnconditionalPositiveRegard = 'unconditional-positive-regard',
  StartButton = 'start-button',
  Raffle = 'raffle',
  QRCode = 'qr-code',
  JeopardAI = 'jeopard-ai',
  MusicalKeys = 'musical-keys',
  HeadToHead = 'head-to-head',
  Evaluate = 'evaluate',
  PhotoSequences = 'photo-sequences',
}

export enum ActivityUserSelectInstruction {
  OnePerTeam = 'one-per-team',
  OnePerRoom = 'one-per-room',
  All = 'all',
  None = 'none',
}

type ActivityHint = { text: string; points: number }

export type ActivitySettings = {
  hints: ActivityHint[]
  wager?: boolean
}

export type RootActivity = {
  id: number
  name: string
  title?: Maybe<string>
  type: ActivityType
  points?: Maybe<number>
  minPeople?: Maybe<number>
  maxPeople?: Maybe<number>
  status: Status
  description?: Maybe<string>
  tries?: Maybe<number>
  userSelectInstruction: ActivityUserSelectInstruction
  speedMatters: boolean
  unlimitedTeams?: boolean
  settings: ActivitySettings
  tags: string[]
  originId?: ActivityId | null
  origin?: Activity
  createdAt: string
  updatedAt: string
  updatedBy?: Maybe<User>
  version: number
  workspaceId?: number
  scoped: boolean
}

export type ActivityActivityGroup = {
  id: number
  activityGroup: ActivityGroup
}

export type ActivityRoom = {
  id: number
  room: Room
}

export type Activity = RootActivity & {
  steps: ActivityStep[]
  media: ActivityMedia[]
  activityGroups: ActivityActivityGroup[]
  rooms: ActivityRoom[]
}

export type CascadeActivity = Cascadify<RootActivity> & {
  steps: CascadeActivityStep[]
  media: CascadeActivityMedia[]
}

export enum RelationType {
  Default = 'default',
  Source = 'source',
  Correct = 'correct',
  Incorrect = 'incorrect',
}

export enum ActivityRelationSettingsType {
  POLL = 'POLL',
  TRW = 'TRW',
  BREAKOUT_POLL = 'BREAKOUT_POLL',
  POLL_SOURCE = 'POLL_SOURCE',
}

export type ActivityRelationSettingsBreakoutPoll = {
  type: ActivityRelationSettingsType.BREAKOUT_POLL
  different?: Maybe<boolean>
  name?: Maybe<string>
}

export type ActivityRelationSettings =
  | {
      type: ActivityRelationSettingsType.POLL
      answerId: string
    }
  | { type: ActivityRelationSettingsType.TRW }
  | { type: ActivityRelationSettingsType.POLL_SOURCE }
  | ActivityRelationSettingsBreakoutPoll
  | Record<string, never>

export type ActivityId = Activity['id']
